// ============================================================================
// MÓDULO 7 - UNIDAD 1: TALKING ABOUT JOBS
// ============================================================================
// Nivel: B1-B2 (Upper-Intermediate)
// Gramática: Job vs Work (Noun/Verb differentiation)
// Vocabulario: Professional careers, employment terms
// Contexto: International work environments
// ============================================================================

export const M7_UNIT_1 = {
  id: "m7-u1",
  title: "Unit 1: Talking about Jobs",
  topic: "Learn the difference between job and work, professional vocabulary, and how to discuss careers confidently!",
  
  grammarImage: "/images/grammar/m7-u1-job-work.jpg",
  grammarText: `
    <h3>Job vs Work - The Essential Difference</h3>
    
    <div class="grammar-rule">
      <h4>JOB (Countable Noun)</h4>
      <p>A specific position or role you have:</p>
      <ul>
        <li>✅ "I have a good <strong>job</strong>" (specific position)</li>
        <li>✅ "She has two <strong>jobs</strong>" (can be plural)</li>
        <li>✅ "He got a new <strong>job</strong> at Google"</li>
      </ul>
    </div>

    <div class="grammar-rule">
      <h4>WORK (Uncountable Noun / Verb)</h4>
      <p><strong>As a noun:</strong> General activity or tasks (no plural):</p>
      <ul>
        <li>✅ "I enjoy my <strong>work</strong>" (general activity)</li>
        <li>✅ "The <strong>work</strong> is challenging"</li>
        <li>❌ "I have two works" (INCORRECT)</li>
      </ul>
      
      <p><strong>As a verb:</strong> The action of working:</p>
      <ul>
        <li>✅ "I <strong>work</strong> in an office"</li>
        <li>✅ "She <strong>works</strong> as a surgeon"</li>
        <li>❌ "I job in an office" (INCORRECT)</li>
      </ul>
    </div>

    <div class="key-difference">
      <p><strong>Quick Rule:</strong></p>
      <p>🔹 <strong>JOB</strong> = specific role (a/an, two, three jobs)</p>
      <p>🔹 <strong>WORK</strong> = activity (no article, no plural) OR verb</p>
    </div>
  `,

  iconType: "briefcase",

  sets: [
    // ========================================================================
    // SET 1: JOB VS WORK - BASIC DISTINCTION
    // ========================================================================
    {
      id: "m7-u1-s1",
      title: "Set 1: Job vs Work - Understanding the Basics",
      questions: [
        {
          type: "mc",
          q: "Which sentence is correct?",
          options: [
            "I enjoy my job as a software engineer",
            "I enjoy my work as a software engineer",
            "Both are correct"
          ],
          a: "Both are correct",
          tip: "💡 SUBTLE DIFFERENCE!\n\nBoth correct, slight nuance:\n• 'My job' = emphasizes the POSITION\n• 'My work' = emphasizes the ACTIVITY/TASKS\n\nNative speakers use both naturally!",
          successtip: "✓ YOU GET IT!\nJob = position 💼\nWork = activity 📊\nBoth work here, small nuance only!"
        },
        {
          type: "mc",
          q: "Complete: 'She has three _____ to pay for university.'",
          options: ["jobs", "works", "work"],
          a: "jobs",
          successtip: "✓ COUNTABLE!\nJob = countable noun 📊\nCan say: one job, two jobs, three jobs ✅"
        },
        {
          type: "mc",
          q: "Which is INCORRECT?",
          options: [
            "I work in marketing",
            "I have an interesting work",
            "I love my work"
          ],
          a: "I have an interesting work",
          tip: "🚫 COMMON ERROR!\n\n❌ 'An interesting work' = WRONG\n✅ 'An interesting job' = CORRECT\n✅ 'Interesting work' = CORRECT (no article)\n\nWork as noun = uncountable (like 'water', 'information')!",
          successtip: "✓ ERROR CAUGHT!\nWork = uncountable noun 🚫\nNever use 'a/an' with work as noun!"
        },
        {
          type: "input",
          q: "I ___ as a graphic designer for an international company.",
          a: "work",
          accept: { ignoreCase: true, alts: ["am working", "'m working"] },
          successtip: "✓ VERB FORM!\nWork = verb here 💼\nOnly 'work' can be a verb, NOT 'job'!"
        },
        {
          type: "mc",
          q: "Complete: 'He's looking for ___ in the medical field.'",
          options: ["a job", "a work", "works"],
          a: "a job",
          successtip: "✓ SPECIFIC POSITION!\nLooking for a job = seeking specific role 🎯\nCountable noun needs article!"
        },
        {
          type: "mc",
          q: "'The work is very demanding this month.' What does 'work' mean here?",
          options: [
            "The tasks and activities",
            "The specific position",
            "The workplace"
          ],
          a: "The tasks and activities",
          tip: "📊 WORK AS NOUN!\n\nWhen 'work' is a noun (not verb):\n→ It means the TASKS/ACTIVITIES you do\n→ Uncountable (like 'homework', 'housework')\n→ Focus on WHAT you do, not WHERE\n\nCompare: 'The job is demanding' (the position itself)",
          successtip: "✓ ACTIVITY MEANING!\nWork (noun) = tasks/activities 📋\nNot about the position, about what you DO!"
        },
        {
          type: "order",
          q: "Put the words in order: 'hospital / works / a / she / in / as / nurse'",
          a: "She works in a hospital as a nurse",
          successtip: "✓ PERFECT STRUCTURE!\nSubject + work (verb) + location + as + profession 💉"
        },
        {
          type: "input",
          q: "My brother has a really stressful ___ at a law firm.",
          a: "job",
          accept: { ignoreCase: true, alts: ["position", "role"] },
          successtip: "✓ SPECIFIC POSITION!\nStressful job = specific role 👔\nCountable noun with article 'a'!"
        },
        {
          type: "mc",
          q: "Which sentence uses 'work' as a VERB?",
          options: [
            "The work is finished",
            "I work remotely",
            "This is interesting work"
          ],
          a: "I work remotely",
          successtip: "✓ VERB IDENTIFIED!\n'I work' = verb (action) 🎬\nOther options = noun (thing) 📄"
        },
        {
          type: "mc",
          q: "Complete naturally: 'I'm tired of my ___. I need a change!'",
          options: ["job", "work", "Either works naturally"],
          a: "Either works naturally",
          tip: "🎯 BOTH NATURAL!\n\nIn this context, both sound native:\n• 'Tired of my job' = want different position\n• 'Tired of my work' = want different tasks/field\n\nSlight difference, both common!\n\nThis shows mastery: knowing when both work! 🌟",
          successtip: "✓ ADVANCED UNDERSTANDING!\nYou recognize BOTH are natural! 🌟\nContext determines slight nuance only!"
        }
      ]
    },

    // ========================================================================
    // SET 2: JOB VS WORK - ADVANCED USAGE
    // ========================================================================
    {
      id: "m7-u1-s2",
      title: "Set 2: Job vs Work - Advanced Contexts",
      questions: [
        {
          type: "mc",
          q: "In a job interview, which is more appropriate? 'Tell me about your previous ___.'",
          options: ["job", "work", "Both equally appropriate"],
          a: "work",
          tip: "💼 INTERVIEW TIP!\n\nIn formal contexts:\n• 'Previous work' = more professional (focus on experience)\n• 'Previous job' = also correct (focus on position)\n\nBut 'work' sounds slightly more sophisticated in interviews!\n\nNative speakers notice these subtleties! 🎯",
          successtip: "✓ PROFESSIONAL CHOICE!\n'Previous work' = sophisticated ✨\nShows formal register awareness!"
        },
        {
          type: "input",
          q: "After finishing university, Maria found ___ as an architect in Dubai.",
          a: "work",
          accept: { ignoreCase: true, alts: ["a job", "employment"] },
          successtip: "✓ NATURAL EXPRESSION!\n'Found work' = common collocation 🌍\nCan also say 'found a job'!"
        },
        {
          type: "mc",
          q: "Which is INCORRECT?",
          options: [
            "I'm between jobs right now",
            "I'm between works right now",
            "I'm looking for work right now"
          ],
          a: "I'm between works right now",
          successtip: "✓ FIXED EXPRESSION!\n'Between jobs' = idiom (unemployed temporarily) 🔄\nNever 'between works'!"
        },
        {
          type: "mc",
          q: "'He works in IT' vs 'He has a job in IT'. What's the difference?",
          options: [
            "First is verb, second is noun - same meaning",
            "First is permanent, second is temporary",
            "No difference, interchangeable"
          ],
          a: "First is verb, second is noun - same meaning",
          tip: "🎓 GRAMMATICAL DIFFERENCE!\n\n'Works in' = VERB (the action)\n'Has a job in' = NOUN (the thing he has)\n\nMeaning is the same, structure differs!\n\nBoth equally common in conversation.",
          successtip: "✓ GRAMMAR INSIGHT!\nSame meaning, different structure 📐\nVerb vs noun - you understand both!"
        },
        {
          type: "order",
          q: "Order: 'enjoy / really / do / your / you / work / ?'",
          a: "Do you really enjoy your work?",
          successtip: "✓ PERFECT QUESTION!\nQuestion word order + work as noun 📋"
        },
        {
          type: "mc",
          q: "Complete: 'Finding ___ in this economy is really challenging.'",
          options: ["work", "a job", "Both are correct"],
          a: "Both are correct",
          successtip: "✓ BOTH WORK!\n'Finding work' = common ✅\n'Finding a job' = common ✅\nBoth natural in this context!"
        },
        {
          type: "input",
          q: "The company is hiring. They have several ___ available in different departments.",
          a: "jobs",
          accept: { ignoreCase: true, alts: ["positions", "openings", "vacancies"] },
          successtip: "✓ PLURAL POSITIONS!\nMultiple jobs = several positions 📊\nCountable noun in plural!"
        },
        {
          type: "mc",
          q: "Which sounds most natural? 'I love ___ I do every day.'",
          options: ["the work", "the job", "work"],
          a: "the work",
          tip: "🎯 NATURAL FLOW!\n\n'The work I do' = most natural\n'The job I do' = less common (but not wrong)\n'Work I do' = missing article\n\nWith relative clause ('I do'), use 'the' + noun!\n\nSmall detail, big impact on fluency! ✨",
          successtip: "✓ NATURAL ENGLISH!\n'The work I do' = perfect flow 🌊\nArticle needed before relative clause!"
        },
        {
          type: "mc",
          q: "'Out of work' means:",
          options: [
            "Unemployed",
            "Outside the office",
            "Finished working for the day"
          ],
          a: "Unemployed",
          successtip: "✓ IDIOM!\n'Out of work' = unemployed 📉\nFixed expression with 'work' (not 'job')!"
        },
        {
          type: "mc",
          q: "Complete: 'She quit her ___ to travel around Asia for a year.'",
          options: ["job", "work", "Both sound equally natural"],
          a: "job",
          tip: "💡 COLLOCATIONS MATTER!\n\n'Quit your job' = most common ✅\n'Quit your work' = less natural ⚠️\n\nWhy? 'Job' = specific thing you can quit\n'Work' = activity (harder to 'quit' an activity)\n\nNative speakers prefer 'quit + job'! 🎯",
          successtip: "✓ COLLOCATION!\n'Quit your job' = natural phrase 💼\nMore common than 'quit your work'!"
        }
      ]
    },

    // ========================================================================
    // SET 3: PROFESSIONAL VOCABULARY - TECHNICAL PROFESSIONS
    // ========================================================================
    {
      id: "m7-u1-s3",
      title: "Set 3: Technical & Specialized Professions",
      questions: [
        {
          type: "mc",
          q: "An 'electrician' is someone who:",
          options: [
            "Installs and repairs electrical systems",
            "Teaches about electricity",
            "Sells electrical appliances"
          ],
          a: "Installs and repairs electrical systems",
          successtip: "✓ TECHNICAL PROFESSION!\nElectrician = works with wiring & electrical systems ⚡"
        },
        {
          type: "input",
          q: "When the sink was blocked, we called a professional ___ to fix the pipes.",
          a: "plumber",
          accept: { ignoreCase: true },
          tip: "🔧 SKILLED TRADE!\n\nPlumber = specialist in:\n• Water pipes\n• Drainage systems  \n• Bathroom/kitchen fixtures\n\nEssential profession in construction and maintenance!\n\nPronunciation tip: Silent 'b' → /ˈplʌmər/",
          successtip: "✓ SKILLED TRADE!\nPlumber = pipe & water systems expert 🚰"
        },
        {
          type: "mc",
          q: "A 'surgeon' works in:",
          options: [
            "Medical field, performing operations",
            "Legal field, in courtrooms",
            "Construction, building structures"
          ],
          a: "Medical field, performing operations",
          successtip: "✓ MEDICAL SPECIALIST!\nSurgeon = performs surgical operations 🏥"
        },
        {
          type: "mc",
          q: "Which profession requires the MOST years of education?",
          options: ["Surgeon", "Electrician", "Plumber"],
          a: "Surgeon",
          tip: "🎓 EDUCATION LEVELS!\n\nSurgeon: 10-15 years (medical degree + specialization)\nElectrician: 4-5 years (apprenticeship + certification)\nPlumber: 4-5 years (apprenticeship + licensing)\n\nAll valuable, different paths! Different education ≠ different worth! 💪",
          successtip: "✓ EDUCATION INSIGHT!\nSurgeon = longest training (10+ years) 📚\nAll professions valuable!"
        },
        {
          type: "order",
          q: "Order: 'as / works / surgeon / a / she / children's / hospital / at / a'",
          a: "She works as a surgeon at a children's hospital",
          successtip: "✓ PROFESSIONAL DESCRIPTION!\nPerfect structure: work as [profession] at [place] 🏥"
        },
        {
          type: "mc",
          q: "Complete: 'My uncle is a licensed ___. He installs solar panels and home wiring.'",
          options: ["electrician", "plumber", "engineer"],
          a: "electrician",
          successtip: "✓ CONTEXT CLUE!\nSolar panels + wiring = electrician's work ☀️⚡"
        },
        {
          type: "input",
          q: "A neurosurgeon is a type of ___ who specializes in brain operations.",
          a: "surgeon",
          accept: { ignoreCase: true, alts: ["doctor"] },
          successtip: "✓ SPECIALIZATION!\nNeurosurgeon = brain surgeon 🧠\nHighly specialized medical profession!"
        },
        {
          type: "mc",
          q: "Which profession typically works with their hands and tools?",
          options: [
            "All three: electrician, plumber, and surgeon",
            "Only electrician and plumber",
            "Only surgeon"
          ],
          a: "All three: electrician, plumber, and surgeon",
          tip: "🛠️ SKILLED HANDS!\n\nAll three work with tools:\n• Electrician: wire strippers, voltage testers\n• Plumber: wrenches, pipe cutters  \n• Surgeon: scalpels, surgical instruments\n\nAll require manual dexterity and precision! 👐",
          successtip: "✓ SKILLED PROFESSIONS!\nAll use specialized tools & manual skill 🛠️\nDifferent fields, same precision needed!"
        },
        {
          type: "mc",
          q: "'He apprenticed as an electrician for four years.' What does 'apprenticed' mean?",
          options: [
            "Learned the trade by working with experienced professionals",
            "Studied in university",
            "Watched online videos"
          ],
          a: "Learned the trade by working with experienced professionals",
          successtip: "✓ APPRENTICESHIP!\nApprentice = learn by doing with expert guidance 👨‍🏫\nCommon in technical trades!"
        },
        {
          type: "mc",
          q: "In a hospital emergency room, which professional would treat a patient with a severe injury requiring immediate operation?",
          options: ["Surgeon", "Electrician", "Plumber"],
          a: "Surgeon",
          successtip: "✓ MEDICAL EMERGENCY!\nSurgeon = performs emergency operations 🚨\nLife-saving profession!"
        }
      ]
    },

    // ========================================================================
    // SET 4: MORE TECHNICAL PROFESSIONS & DESCRIPTIONS
    // ========================================================================
    {
      id: "m7-u1-s4",
      title: "Set 4: Describing Professional Roles",
      questions: [
        {
          type: "input",
          q: "An ___ designs and builds roads, bridges, and buildings using math and science.",
          a: "engineer",
          accept: { ignoreCase: true },
          successtip: "✓ ENGINEERING!\nEngineer = applies science to build things 🌉"
        },
        {
          type: "mc",
          q: "Complete: 'She works ___ a cardiac surgeon at the national hospital.'",
          options: ["as", "like", "for"],
          a: "as",
          tip: "📐 'WORK AS' STRUCTURE!\n\n✅ 'Work AS' = your profession/role\n'She works AS a teacher'\n\n❌ 'Work LIKE' = incorrect\n❌ 'Work FOR' = your employer\n'She works FOR Google'\n\nDifferent prepositions, different meanings!",
          successtip: "✓ CORRECT PREPOSITION!\n'Work as' = profession 💼\n'Work for' = employer 🏢"
        },
        {
          type: "mc",
          q: "What does a 'certified electrician' mean?",
          options: [
            "Officially qualified with proper training and license",
            "Very experienced but no formal training",
            "Works only with certificates"
          ],
          a: "Officially qualified with proper training and license",
          successtip: "✓ CERTIFICATION!\nCertified = officially qualified with credentials ✅\nImportant for safety!"
        },
        {
          type: "order",
          q: "Order: 'hospital / the / plumbers / in / fixed / leak / the / emergency'",
          a: "The plumbers fixed the emergency leak in the hospital",
          successtip: "✓ EMERGENCY WORK!\nEven hospitals need plumbers for infrastructure! 🚰"
        },
        {
          type: "mc",
          q: "Which is the most formal way to describe someone's profession?",
          options: [
            "She is employed as a consultant surgeon",
            "She's a surgeon",
            "She does surgery work"
          ],
          a: "She is employed as a consultant surgeon",
          tip: "🎩 FORMALITY LEVELS!\n\nMost formal → Least formal:\n1. 'Is employed as a consultant surgeon' (very formal)\n2. 'Works as a surgeon' (neutral)\n3. 'Is a surgeon' (casual)\n4. 'Does surgery work' (awkward)\n\nContext determines which to use! 📊",
          successtip: "✓ FORMAL REGISTER!\n'Is employed as' = very professional language 🎓"
        },
        {
          type: "input",
          q: "After years of training, he finally became a qualified ___ and opened his own electrical contracting business.",
          a: "electrician",
          accept: { ignoreCase: true },
          successtip: "✓ CAREER PROGRESSION!\nQualified electrician → business owner 📈\nProfessional development!"
        },
        {
          type: "mc",
          q: "'She's a skilled tradesperson.' What does this mean?",
          options: [
            "She has specialized manual skills in a technical profession",
            "She's good at trading stocks",
            "She sells professional tools"
          ],
          a: "She has specialized manual skills in a technical profession",
          successtip: "✓ SKILLED TRADE!\nTradesperson = technical specialist (plumber, electrician, etc.) 🔧"
        },
        {
          type: "mc",
          q: "Which profession would you call for a burst water pipe?",
          options: ["Plumber", "Electrician", "Surgeon"],
          a: "Plumber",
          successtip: "✓ RIGHT SPECIALIST!\nWater emergency = plumber's expertise 💧"
        },
        {
          type: "input",
          q: "The hospital hired three new ___ to handle the increasing number of complex operations.",
          a: "surgeons",
          accept: { ignoreCase: true },
          successtip: "✓ HIRING SPECIALISTS!\nPlural profession: surgeon → surgeons 🏥"
        },
        {
          type: "mc",
          q: "Complete: 'Working ___ an electrician requires both physical strength and technical knowledge.'",
          options: ["as", "like", "for"],
          a: "as",
          successtip: "✓ GERUND + AS!\n'Working as' = profession ⚡\nGerund form for general statements!"
        }
      ]
    },

    // ========================================================================
    // SET 5: EMPLOYMENT TERMS - TRAINEE & INTERN
    // ========================================================================
    {
      id: "m7-u1-s5",
      title: "Set 5: Employment Status - Entry Level",
      questions: [
        {
          type: "mc",
          q: "A 'trainee' is someone who:",
          options: [
            "Is learning a job through formal training in a company",
            "Trains other employees",
            "Works on trains"
          ],
          a: "Is learning a job through formal training in a company",
          tip: "🌱 TRAINEE = LEARNING!\n\nTrainee characteristics:\n• Entry-level position\n• Formal training program\n• Usually paid (though less than full position)\n• Goal: become full employee\n\nExample: 'management trainee', 'sales trainee'\n\nIt's a stepping stone in your career! 📈",
          successtip: "✓ ENTRY LEVEL!\nTrainee = learning on the job 📚\nFormal company training program!"
        },
        {
          type: "mc",
          q: "An 'intern' typically:",
          options: [
            "Works temporarily to gain experience, often unpaid",
            "Is a permanent senior employee",
            "Only works at night"
          ],
          a: "Works temporarily to gain experience, often unpaid",
          successtip: "✓ INTERNSHIP!\nIntern = temporary position for experience 🎓\nCommon for students/recent graduates!"
        },
        {
          type: "input",
          q: "She's working as an ___ at a law firm this summer to gain practical experience before graduation.",
          a: "intern",
          accept: { ignoreCase: true, alts: ["internship"] },
          successtip: "✓ SUMMER INTERNSHIP!\nIntern = temporary work experience 📝\nCommon before graduation!"
        },
        {
          type: "mc",
          q: "What's the main difference between a trainee and an intern?",
          options: [
            "Trainees are usually company employees; interns are often students",
            "Trainees are always unpaid; interns are always paid",
            "No real difference"
          ],
          a: "Trainees are usually company employees; interns are often students",
          tip: "🎯 KEY DIFFERENCES!\n\nTRAINEE:\n• Company employee (entry-level)\n• Usually paid salary\n• Longer term (6 months-2 years)\n• Formal company program\n\nINTERN:\n• Often student/recent grad\n• May be unpaid or low pay\n• Shorter term (summer/semester)\n• Gain experience for resume\n\nBoth valuable career starts! 🚀",
          successtip: "✓ UNDERSTANDING ROLES!\nTrainee = employee learning 💼\nIntern = student/grad gaining experience 🎓"
        },
        {
          type: "order",
          q: "Order: 'as / started / trainee / marketing / a / he / after / university'",
          a: "He started as a marketing trainee after university",
          successtip: "✓ CAREER START!\nPerfect entry-level beginning 🌟\nTrainee → career progression!"
        },
        {
          type: "mc",
          q: "'She's doing an internship at the UN.' This means:",
          options: [
            "She's working there temporarily to gain experience",
            "She has a permanent job there",
            "She's interviewing there"
          ],
          a: "She's working there temporarily to gain experience",
          successtip: "✓ TEMPORARY POSITION!\n'Doing an internship' = temporary work experience 🌍"
        },
        {
          type: "input",
          q: "The company offers a two-year ___ program where recent graduates learn all aspects of the business.",
          a: "trainee",
          accept: { ignoreCase: true, alts: ["training"] },
          successtip: "✓ DEVELOPMENT PROGRAM!\nTrainee program = structured learning path 📊"
        },
        {
          type: "mc",
          q: "Which position usually leads to a permanent job in the same company?",
          options: ["Trainee", "Intern", "Both equally likely"],
          a: "Trainee",
          tip: "💼 CAREER PATH!\n\nTRAINEE → Usually becomes permanent employee\n• Already on company payroll\n• Designed to fill future positions\n• Higher conversion rate\n\nINTERN → May become permanent, but less common\n• Often returns to studies\n• Shorter commitment\n• More exploratory\n\nBoth valuable, different purposes! 🎯",
          successtip: "✓ CAREER TRAJECTORY!\nTrainee = designed for permanent role 📈\nHigher chance of staying!"
        },
        {
          type: "mc",
          q: "Complete: 'After completing her ___ at the hospital, they offered her a full-time position.'",
          options: ["internship", "training", "Both could work"],
          a: "Both could work",
          successtip: "✓ BOTH NATURAL!\nInternship or training = both lead to job offers ✅"
        },
        {
          type: "mc",
          q: "'He's a graduate trainee in the finance department.' This suggests:",
          options: [
            "He recently graduated and is in an entry-level development program",
            "He trains graduates",
            "He's studying finance"
          ],
          a: "He recently graduated and is in an entry-level development program",
          successtip: "✓ GRADUATE PROGRAM!\nGraduate trainee = recent university grad in company training 🎓💼"
        }
      ]
    },

    // ========================================================================
    // SET 6: EMPLOYMENT TERMS - SALARY & OVERTIME
    // ========================================================================
    {
      id: "m7-u1-s6",
      title: "Set 6: Compensation & Working Hours",
      questions: [
        {
          type: "mc",
          q: "What is a 'salary'?",
          options: [
            "A fixed regular payment, usually monthly or yearly",
            "Payment only for hours worked",
            "A one-time bonus"
          ],
          a: "A fixed regular payment, usually monthly or yearly",
          tip: "💰 SALARY VS WAGE!\n\nSALARY:\n• Fixed amount (e.g., $50,000/year)\n• Usually monthly payment\n• Doesn't change with hours\n• Professional/office jobs\n\nWAGE:\n• Hourly rate (e.g., $15/hour)\n• Changes with hours worked\n• Paid weekly/bi-weekly\n• Manual/service jobs\n\nDifferent payment structures! 📊",
          successtip: "✓ COMPENSATION!\nSalary = fixed annual/monthly payment 💵\nDoesn't vary with hours!"
        },
        {
          type: "mc",
          q: "'Overtime' means:",
          options: [
            "Extra hours worked beyond normal schedule, usually with extra pay",
            "Being late to work",
            "Taking too long on breaks"
          ],
          a: "Extra hours worked beyond normal schedule, usually with extra pay",
          successtip: "✓ EXTRA HOURS!\nOvertime = work beyond regular hours ⏰\nUsually paid at higher rate!"
        },
        {
          type: "input",
          q: "She earns a competitive ___ of $75,000 per year, plus benefits.",
          a: "salary",
          accept: { ignoreCase: true, alts: ["wage"] },
          successtip: "✓ ANNUAL COMPENSATION!\nCompetitive salary = good pay for the position 💰"
        },
        {
          type: "mc",
          q: "Complete: 'I've been working a lot of ___ this month because we have a big project deadline.'",
          options: ["overtime", "overwork", "extra time"],
          a: "overtime",
          tip: "⏰ OVERTIME COLLOCATIONS!\n\n✅ 'Work overtime' = standard phrase\n✅ 'Do overtime' = also common\n❌ 'Work overwork' = incorrect\n❌ 'Work extra time' = not standard\n\nOvertime is the fixed term in business English! 📊",
          successtip: "✓ BUSINESS TERM!\n'Work overtime' = standard expression ⏰\nFixed collocation!"
        },
        {
          type: "order",
          q: "Order: 'overtime / paid / for / is / at / she / rate / 1.5 / times / the / normal'",
          a: "She is paid at 1.5 times the normal rate for overtime",
          successtip: "✓ OVERTIME PAY!\n1.5x rate = 'time and a half' 💰\nStandard overtime compensation!"
        },
        {
          type: "mc",
          q: "'He negotiated a higher salary before accepting the job offer.' What does 'negotiated' mean?",
          options: [
            "Discussed and reached an agreement on",
            "Demanded without discussion",
            "Rejected"
          ],
          a: "Discussed and reached an agreement on",
          successtip: "✓ NEGOTIATION!\nNegotiate = discuss to reach agreement 🤝\nCommon in job offers!"
        },
        {
          type: "input",
          q: "Many employees prefer a good work-life balance over constantly working ___ for extra money.",
          a: "overtime",
          accept: { ignoreCase: true },
          successtip: "✓ WORK-LIFE BALANCE!\nConstant overtime = reduced quality of life ⚖️\nBalance is important!"
        },
        {
          type: "mc",
          q: "Which statement about salary is TRUE?",
          options: [
            "Salary is typically quoted as an annual figure",
            "Salary changes every week based on hours",
            "Salary is always paid daily"
          ],
          a: "Salary is typically quoted as an annual figure",
          tip: "💼 SALARY CONVENTIONS!\n\nCommon ways to state salary:\n• Annual: '$60,000 per year'\n• Monthly: '$5,000 per month'\n• Never daily or hourly (that's wage)\n\nIn job ads: 'Salary: $50K-$70K' = annual\n\nProfessional standard! 📊",
          successtip: "✓ ANNUAL FIGURE!\nSalary = yearly amount ($XX,000/year) 📅"
        },
        {
          type: "mc",
          q: "'Time and a half' for overtime means:",
          options: [
            "You're paid 1.5 times your normal hourly rate",
            "You work 1.5 times longer",
            "You get an extra half day off"
          ],
          a: "You're paid 1.5 times your normal hourly rate",
          successtip: "✓ OVERTIME RATE!\n'Time and a half' = 1.5x normal pay 💵\nCommon overtime compensation!"
        },
        {
          type: "mc",
          q: "Complete: 'The starting ___ for this position is $45,000, with opportunities for increases based on performance.'",
          options: ["salary", "overtime", "work"],
          a: "salary",
          successtip: "✓ JOB OFFER LANGUAGE!\n'Starting salary' = initial compensation 💰\nIncreases with performance!"
        }
      ]
    },

    // ========================================================================
    // SET 7: CAREER TRAJECTORY - DISCUSSING CAREER PATHS
    // ========================================================================
    {
      id: "m7-u1-s7",
      title: "Set 7: Career Paths & Professional Development",
      questions: [
        {
          type: "mc",
          q: "'Career trajectory' means:",
          options: [
            "The path and progression of your professional life",
            "Your daily work schedule",
            "The distance you travel to work"
          ],
          a: "The path and progression of your professional life",
          tip: "📈 CAREER TRAJECTORY!\n\nTrajectory = path/direction over time\n\nExamples:\n• 'Upward trajectory' = getting promotions\n• 'Flat trajectory' = staying at same level\n• 'Career pivot' = changing direction\n\nIt's your professional journey! 🛤️\n\nThink long-term, not just current job!",
          successtip: "✓ CAREER PLANNING!\nTrajectory = professional path over time 📈\nYour career journey!"
        },
        {
          type: "input",
          q: "After starting as an intern, she worked her way up to senior manager - an impressive career ___.",
          a: "trajectory",
          accept: { ignoreCase: true, alts: ["progression", "path", "journey"] },
          successtip: "✓ CAREER PROGRESSION!\nIntern → Senior Manager = successful trajectory 🌟"
        },
        {
          type: "mc",
          q: "'I'm looking to advance in my career.' This means:",
          options: [
            "I want to move to a higher position",
            "I want to change industries completely",
            "I want to retire early"
          ],
          a: "I want to move to a higher position",
          successtip: "✓ CAREER AMBITION!\nAdvance = move up/get promoted 📊\nProfessional growth!"
        },
        {
          type: "order",
          q: "Order: 'career / her / switch / decided / to / midway / through / she / paths'",
          a: "She decided to switch career paths midway through her career",
          successtip: "✓ CAREER CHANGE!\nSwitch career paths = change profession 🔄\nMidway = middle of career!"
        },
        {
          type: "mc",
          q: "What does it mean to 'climb the corporate ladder'?",
          options: [
            "To get promoted through increasingly senior positions",
            "To physically climb stairs at work",
            "To work in a tall building"
          ],
          a: "To get promoted through increasingly senior positions",
          tip: "🪜 CAREER IDIOM!\n\n'Climb the corporate ladder' = metaphor for career progression\n\nLadder rungs = job levels:\n• Bottom: Entry-level\n• Middle: Manager\n• Top: Executive/Director\n\nEach promotion = climbing one rung!\n\nCommon business idiom! 💼",
          successtip: "✓ BUSINESS METAPHOR!\nClimb the ladder = get promoted step by step 🪜\nClassic career idiom!"
        },
        {
          type: "mc",
          q: "Complete: 'She has 15 years of ___ in financial management.'",
          options: ["experience", "experiences", "experiment"],
          a: "experience",
          successtip: "✓ PROFESSIONAL EXPERIENCE!\nExperience = uncountable in this context 📚\nYears of experience = career history!"
        },
        {
          type: "input",
          q: "He started his ___ as a junior developer and now he's the Chief Technology Officer.",
          a: "career",
          accept: { ignoreCase: true, alts: ["journey", "path"] },
          successtip: "✓ CAREER JOURNEY!\nJunior → CTO = impressive progression! 🚀"
        },
        {
          type: "mc",
          q: "'Professional development' refers to:",
          options: [
            "Learning new skills and knowledge to advance your career",
            "Building construction projects",
            "Developing software professionally"
          ],
          a: "Learning new skills and knowledge to advance your career",
          tip: "📚 CONTINUOUS LEARNING!\n\nProfessional development includes:\n• Training courses\n• Conferences\n• Certifications\n• Workshops\n• Mentorship\n• New skills\n\nInvesting in yourself = investing in your career! 💪\n\nNever stop learning!",
          successtip: "✓ CAREER GROWTH!\nProfessional development = ongoing learning 📚\nKey to career success!"
        },
        {
          type: "mc",
          q: "What does 'make a career change' mean?",
          options: [
            "Switch to a different profession or industry",
            "Get a promotion",
            "Change offices"
          ],
          a: "Switch to a different profession or industry",
          successtip: "✓ MAJOR CHANGE!\nCareer change = switch professions 🔄\nBig decision, new direction!"
        },
        {
          type: "mc",
          q: "Complete: 'Taking on that challenging project was a real turning ___ in my career.'",
          options: ["point", "place", "time"],
          a: "point",
          successtip: "✓ CAREER MILESTONE!\n'Turning point' = moment that changed direction 🎯\nFixed expression!"
        }
      ]
    },

    // ========================================================================
    // SET 8: CAREER DISCUSSIONS - TALKING ABOUT WORK HISTORY
    // ========================================================================
    {
      id: "m7-u1-s8",
      title: "Set 8: Describing Work Experience",
      questions: [
        {
          type: "mc",
          q: "'I've been in this field for ten years.' What does 'field' mean here?",
          options: [
            "Area of professional work or industry",
            "An outdoor area",
            "A sports field"
          ],
          a: "Area of professional work or industry",
          successtip: "✓ PROFESSIONAL FIELD!\nField = area of work/industry 🏢\nExample: 'marketing field', 'medical field'"
        },
        {
          type: "input",
          q: "Before joining this company, I worked ___ various startups in Silicon Valley.",
          a: "at",
          accept: { ignoreCase: true, alts: ["for", "with"] },
          tip: "📍 PREPOSITION CHOICE!\n\n'Work AT' = general location\n'I worked at Google' ✅\n\n'Work FOR' = emphasizes employer\n'I worked for a tech company' ✅\n\n'Work IN' = industry/building\n'I work in marketing' ✅\n\nAll correct, slight nuance! Native speakers use all three.",
          successtip: "✓ PREPOSITION!\nWork at/for = both natural 💼\nContext determines best choice!"
        },
        {
          type: "mc",
          q: "'I transitioned from teaching to corporate training.' What does 'transitioned' mean?",
          options: [
            "Changed or moved from one thing to another",
            "Stayed in the same job",
            "Got fired"
          ],
          a: "Changed or moved from one thing to another",
          successtip: "✓ CAREER TRANSITION!\nTransition = smooth change from one role to another 🔄\nProfessional vocabulary!"
        },
        {
          type: "order",
          q: "Order: 'in / gained / I've / valuable / experience / industry / this'",
          a: "I've gained valuable experience in this industry",
          successtip: "✓ PRESENT PERFECT!\nGained experience = acquired over time 📈\nPerfect for CV/resume language!"
        },
        {
          type: "mc",
          q: "Which is the most professional way to describe previous employment?",
          options: [
            "I was employed at Microsoft from 2018 to 2022",
            "I did some stuff at Microsoft",
            "I hung out at Microsoft for a while"
          ],
          a: "I was employed at Microsoft from 2018 to 2022",
          tip: "💼 PROFESSIONAL LANGUAGE!\n\nFormal (resume/interview):\n✅ 'Was employed at...'\n✅ 'Worked at... in the capacity of...'\n✅ 'Held the position of...'\n\nInformal (friends):\n'Worked at...' / 'Had a job at...'\n\nKnow your audience! Context is key! 🎯",
          successtip: "✓ PROFESSIONAL REGISTER!\n'Was employed' = formal & precise 🎓\nPerfect for CVs and interviews!"
        },
        {
          type: "mc",
          q: "Complete: 'My background is ___ engineering, but I moved into management.'",
          options: ["in", "at", "for"],
          a: "in",
          successtip: "✓ CORRECT PREPOSITION!\n'Background in' = area of expertise 📚\nFixed expression!"
        },
        {
          type: "input",
          q: "She has extensive ___ working with international teams across multiple time zones.",
          a: "experience",
          accept: { ignoreCase: true },
          successtip: "✓ PROFESSIONAL EXPERIENCE!\nExtensive experience = lots of relevant background 🌍"
        },
        {
          type: "mc",
          q: "'I'm currently between jobs' is a polite way to say:",
          options: [
            "I'm unemployed at the moment",
            "I have two jobs",
            "I'm deciding which job to take"
          ],
          a: "I'm unemployed at the moment",
          tip: "🎯 EUPHEMISM!\n\n'Between jobs' = diplomatic way to say unemployed\n\nOther polite phrases:\n• 'Exploring new opportunities'\n• 'In transition'\n• 'Taking some time off'\n• 'Looking for my next challenge'\n\nAll more positive than 'unemployed'! 💼",
          successtip: "✓ POLITE EXPRESSION!\n'Between jobs' = diplomatic phrasing 🎭\nPositive spin on unemployment!"
        },
        {
          type: "mc",
          q: "What does 'hands-on experience' mean?",
          options: [
            "Practical, direct experience doing something yourself",
            "Experience shaking hands",
            "Theoretical knowledge only"
          ],
          a: "Practical, direct experience doing something yourself",
          successtip: "✓ PRACTICAL EXPERIENCE!\nHands-on = actually doing it, not just learning 🛠️\nHighly valued by employers!"
        },
        {
          type: "mc",
          q: "Complete: 'I'm looking to ___ my skills in a new role.'",
          options: ["leverage", "spend", "forget"],
          a: "leverage",
          successtip: "✓ PROFESSIONAL VERB!\nLeverage = use effectively/strategically 🎯\nCommon in business English!"
        }
      ]
    },

    // ========================================================================
    // SET 9: INTEGRATION - COMBINING GRAMMAR + VOCABULARY
    // ========================================================================
    {
      id: "m7-u1-s9",
      title: "Set 9: Real-World Professional Contexts",
      questions: [
        {
          type: "mc",
          q: "Choose the best answer: 'After her internship, she was offered ___.'",
          options: [
            "a permanent job in the company",
            "a permanent work in the company",
            "permanent works in the company"
          ],
          a: "a permanent job in the company",
          tip: "🎯 COMBINING CONCEPTS!\n\nThis tests multiple things:\n1. Job vs Work (job = countable)\n2. Article usage ('a' needed)\n3. Employment terminology ('permanent')\n4. Natural collocation\n\n'Offered a job' = standard phrase\n'Offered work' = possible but less common\n'Offered works' = incorrect (plural wrong)\n\nMultiple rules working together! 🧩",
          successtip: "✓ INTEGRATION!\nJob (countable) + article + adjective = perfect! 💼\nCombining grammar + vocabulary!"
        },
        {
          type: "input",
          q: "The trainee electrician ___ overtime every weekend to gain more experience on large projects.",
          a: "works",
          accept: { ignoreCase: true, alts: ["does", "is working"] },
          successtip: "✓ COMPLEX SENTENCE!\nTrainee + profession + work (verb) + overtime 🔧⚡\nMultiple concepts together!"
        },
        {
          type: "mc",
          q: "Which sentence correctly uses all the terms?",
          options: [
            "The surgeon's work at the hospital includes training interns and managing her own surgical schedule",
            "The surgeon's job at the hospital includes training interns and managing her own surgical schedule",
            "Both are equally correct and natural"
          ],
          a: "Both are equally correct and natural",
          successtip: "✓ ADVANCED UNDERSTANDING!\nBoth work here - you grasp the nuance! 🌟\nContext allows either!"
        },
        {
          type: "order",
          q: "Order: 'as / started / plumber's / he / trainee / a / before / qualified / becoming / a'",
          a: "He started as a plumber's trainee before becoming a qualified plumber",
          successtip: "✓ CAREER PROGRESSION!\nTrainee → Qualified professional = perfect path 📈🔧"
        },
        {
          type: "mc",
          q: "'She works as a surgical intern at a teaching hospital, where she assists experienced surgeons while learning complex procedures.' This sentence shows:",
          options: [
            "All concepts integrated naturally: work (verb), profession, employment status",
            "Only grammar, no vocabulary",
            "Incorrect usage of terms"
          ],
          a: "All concepts integrated naturally: work (verb), profession, employment status",
          tip: "🎓 FULL INTEGRATION!\n\nBreaking it down:\n✅ 'Works' = verb (not 'jobs')\n✅ 'Surgical intern' = employment status + field\n✅ 'Teaching hospital' = workplace type\n✅ 'Surgeons' = profession\n✅ 'Complex procedures' = field-specific vocab\n\nAll concepts working together naturally! This is fluency! 🌟",
          successtip: "✓ MASTERFUL INTEGRATION! 🌟\nGrammar + vocabulary + professional context!\nThis is real English!"
        },
        {
          type: "mc",
          q: "Complete naturally: 'The company offers excellent training for ___, with opportunities to work on real projects while learning.'",
          options: ["trainees", "trainee", "training"],
          a: "trainees",
          successtip: "✓ PLURAL CONTEXT!\n'For trainees' = multiple people 👥\nPlural noun needed!"
        },
        {
          type: "input",
          q: "He's passionate about his ___ as an electrician, especially when he gets to work on complex renewable energy installations.",
          a: "work",
          accept: { ignoreCase: true, alts: ["job"] },
          successtip: "✓ NATURAL CHOICE!\nBoth work/job possible - you understand both! 💡"
        },
        {
          type: "mc",
          q: "Which sounds most natural in a professional context?",
          options: [
            "The hospital hired three surgical trainees who will work closely with senior staff",
            "The hospital hired three surgical trainees who will job closely with senior staff",
            "The hospital hired three surgical trainings who will work closely with senior staff"
          ],
          a: "The hospital hired three surgical trainees who will work closely with senior staff",
          successtip: "✓ PROFESSIONAL LANGUAGE!\nAll elements correct: trainees + work (verb) + professional context 🏥"
        },
        {
          type: "mc",
          q: "'During my internship, I learned that working as a surgeon requires not just medical knowledge but also excellent communication skills.' This sentence demonstrates:",
          options: [
            "Natural integration of employment terms and career discussion",
            "Too many big words",
            "Incorrect grammar"
          ],
          a: "Natural integration of employment terms and career discussion",
          successtip: "✓ AUTHENTIC ENGLISH!\nInternship + working as + profession + skills = real language 🎯"
        },
        {
          type: "mc",
          q: "Complete: 'The electrical company is looking for qualified ___ who can work independently and handle overtime when necessary.'",
          options: ["electricians", "electrician", "electric"],
          a: "electricians",
          successtip: "✓ COMPLETE INTEGRATION! ⚡\nProfession (plural) + work (verb) + overtime = all concepts! 💼"
        }
      ]
    },

    // ========================================================================
    // SET 10: REAL-WORLD SCENARIOS - PROFESSIONAL SITUATIONS
    // ========================================================================
    {
      id: "m7-u1-s10",
      title: "Set 10: Professional Situations & Conversations",
      questions: [
        {
          type: "mc",
          q: "In a job interview, someone asks: 'Why did you leave your last job?' The most professional response is:",
          options: [
            "I was looking for new challenges and opportunities to grow my career",
            "I hated my boss and the work was boring",
            "They didn't pay me enough money"
          ],
          a: "I was looking for new challenges and opportunities to grow my career",
          tip: "💼 INTERVIEW DIPLOMACY!\n\nProfessional responses focus on:\n✅ Growth opportunities\n✅ New challenges\n✅ Career development\n✅ Better fit\n\nAvoid:\n❌ Criticizing previous employer\n❌ Focusing only on money\n❌ Negative language\n\nEven if true, frame positively! Stay diplomatic! 🎯",
          successtip: "✓ PROFESSIONAL ANSWER!\nFocus on growth, not negativity 📈\nDiplomatic interview language!"
        },
        {
          type: "input",
          q: "At a networking event: 'So, what do you do?' 'I ___ as a senior plumber for a construction company. I specialize in commercial buildings.'",
          a: "work",
          accept: { ignoreCase: true },
          successtip: "✓ NETWORKING LANGUAGE!\n'I work as...' = standard introduction 🤝\nProfessional self-introduction!"
        },
        {
          type: "mc",
          q: "A colleague says: 'I'm thinking about my next career move.' They probably mean:",
          options: [
            "Considering a job change or promotion",
            "Moving to a different city",
            "Changing their desk location"
          ],
          a: "Considering a job change or promotion",
          successtip: "✓ CAREER PLANNING!\n'Career move' = professional change 🎯\nJob change or advancement!"
        },
        {
          type: "order",
          q: "Traduce: Él intentó negociar un mejor salario antes de firmar el contrato",
          a: "He tried to negotiate a better salary before signing the contract",
          successtip: "✓ NEGOTIATION TIMING!\nNegotiate before signing = smart move! 💰📝"
        },
        {
          type: "mc",
          q: "At an exit interview, HR asks: 'What did you enjoy most about working here?' The most constructive response:",
          options: [
            "I really valued the professional development opportunities and working with skilled colleagues",
            "Nothing, that's why I'm leaving",
            "The fact that I could leave early sometimes"
          ],
          a: "I really valued the professional development opportunities and working with skilled colleagues",
          tip: "🎯 EXIT INTERVIEW STRATEGY!\n\nEven when leaving, stay positive:\n✅ Highlight good experiences\n✅ Show appreciation\n✅ Remain professional\n✅ Maintain relationships\n\nWhy?\n• You might need references\n• Industries are small\n• Leave doors open\n• Professional reputation matters\n\nNever burn bridges! 🌉",
          successtip: "✓ PROFESSIONAL EXIT!\nStay positive when leaving 🌟\nMaintain good relationships!"
        },
        {
          type: "mc",
          q: "During a performance review: 'How do you feel about your work this year?' Best response:",
          options: [
            "I'm proud of my achievements and see areas where I can continue to grow",
            "I'm the best employee here",
            "I just do what I'm told"
          ],
          a: "I'm proud of my achievements and see areas where I can continue to grow",
          successtip: "✓ BALANCED RESPONSE!\nAcknowledge strengths + growth mindset = professional 📊"
        },
        {
          type: "input",
          q: "Email context: 'Dear Hiring Manager, I am writing to express my interest in the trainee ___ position advertised on your website. My background in electrical engineering makes me a strong candidate.'",
          a: "electrician",
          accept: { ignoreCase: true },
          successtip: "✓ COVER LETTER!\nTrainee + profession = entry-level position 📧✨"
        },
        {
          type: "mc",
          q: "A mentor asks: 'Where do you see yourself in five years?' Professional response:",
          options: [
            "I hope to have developed advanced skills in my field and taken on more responsibility",
            "I have no idea",
            "Retired on a beach somewhere"
          ],
          a: "I hope to have developed advanced skills in my field and taken on more responsibility",
          tip: "🎯 CAREER VISION QUESTION!\n\nStrong answers show:\n✅ Ambition (but realistic)\n✅ Commitment to field\n✅ Desire to grow\n✅ Professional goals\n\nExample framework:\n'In 5 years, I see myself [skill development] and [increased responsibility] while [contributing to company/field].'\n\nShows planning without being overconfident! 📈",
          successtip: "✓ CAREER PLANNING!\nShows ambition + growth mindset 🚀\nRealistic and professional!"
        },
        {
          type: "mc",
          q: "Conversation: 'How's the new job?' 'Great! The work is challenging, my colleagues are supportive, and the salary is competitive.' This response uses:",
          options: [
            "Work (noun) correctly to describe tasks",
            "Work incorrectly",
            "Should use 'job' instead of 'work'"
          ],
          a: "Work (noun) correctly to describe tasks",
          successtip: "✓ CORRECT USAGE!\n'The work' = the tasks/activities ✅\nPerfectly natural in context!"
        },
        {
          type: "mc",
          q: "Final scenario: 'After completing his surgical internship, Dr. Rodriguez was offered a position as a trainee surgeon with excellent salary and professional development opportunities. He accepted and is now working 60-hour weeks, including significant overtime, but he loves his work and feels his career is on the right trajectory.' How many concepts from this unit appear naturally?",
          options: [
            "Almost all: intern, trainee, surgeon, position, salary, professional development, overtime, work, career, trajectory",
            "Only 2-3 concepts",
            "None, it's all wrong"
          ],
          a: "Almost all: intern, trainee, surgeon, position, salary, professional development, overtime, work, career, trajectory",
          tip: "🌟 UNIT MASTERY!\n\nThis paragraph naturally includes:\n✅ Internship (employment status)\n✅ Surgeon (technical profession)\n✅ Trainee (employment level)\n✅ Position/offered (job terms)\n✅ Salary (compensation)\n✅ Professional development (career growth)\n✅ Overtime (working hours)\n✅ Work (as noun - 'his work')\n✅ Career (trajectory)\n✅ All grammatically correct!\n\nThis is how natives use all these terms together! You've learned real, integrated English! 🎓🌍",
          successtip: "✓ COMPLETE INTEGRATION! 🎉\n\nYou recognize ALL concepts working naturally together!\n\nFrom this unit:\n📚 Job vs Work ✅\n🔧 Technical professions ✅  \n💼 Employment terms ✅\n💰 Salary & overtime ✅\n📈 Career trajectory ✅\n\nYou can now discuss careers professionally in English! 🌟\n\nThis is real-world fluency! 💪🌍"
        }
      ]
    }
  ],

  // ==========================================================================
  // READING SET - CAREER PATHS
  // ==========================================================================
   // ==========================================================================
  // READING SET - CAREER PATHS (CONTINUACIÓN Y COMPLETADO)
  // ==========================================================================
  readingSet: {
    id: "m7-u1-reading",
    title: "Reading Practice - Two Different Career Paths",
    category: "reading",
    
    questions: [
      // ======================================================================
      // BLOQUE 1: Introduction - Two Professionals (PRIMERAS 2 PREGUNTAS)
      // ======================================================================
      {
        type: "passage",
        passage: `**Two Different Career Paths**

Meet Ana and Marcus – two professionals who took very different routes to career success.

**Ana's Journey: From Intern to Executive**

Ana Chen graduated from business school at 22 with dreams of working in international marketing. "I knew I wanted a career in a global company," she says. "But I also knew I needed real-world experience first."

She started as an unpaid intern at a multinational corporation in Singapore, working long hours and often doing overtime without extra pay. "It was tough," Ana admits. "I had another part-time job to pay my rent. But the internship gave me incredible hands-on experience."

After six months, the company offered her a position as a marketing trainee with a modest salary of $35,000 per year. Ana accepted immediately. "It wasn't about the money at that point," she explains. "It was about learning from the best and building my career trajectory."

Over the next decade, Ana worked her way up through the company. She moved from trainee to junior marketer, then to team leader, and eventually to regional director. "I never said no to a challenge," she says. "I took on extra projects, worked overtime when needed, and constantly pursued professional development opportunities."

Today, at 35, Ana is Vice President of Global Marketing, earning a six-figure salary and managing teams across five continents. "My work is demanding," she says, "but I love what I do. Every day brings new challenges."`
      },
      {
        type: "mc",
        q: "What was Ana's first position in the marketing industry?",
        options: [
          "Unpaid intern",
          "Marketing trainee",
          "Regional director"
        ],
        a: "Unpaid intern",
        successtip: "✓ READING DETAIL!\nAna started as unpaid intern → then trainee 📊\nCareer progression from bottom!"
      },
      {
        type: "mc",
        q: "The text uses 'work' and 'job' multiple times. Which statement is TRUE about this usage?",
        options: [
          "Both are used correctly based on context and grammar rules",
          "The author uses them randomly without distinction",
          "Only 'job' should be used throughout"
        ],
        a: "Both are used correctly based on context and grammar rules",
        successtip: "✓ GRAMMAR IN CONTEXT!\nText shows natural job/work usage 📚\nBoth used appropriately throughout!"
      },

      // ======================================================================
      // BLOQUE 2: Marcus's Technical Path (PREGUNTAS 3-4)
      // ======================================================================
      {
        type: "passage",
        passage: `**Marcus's Path: The Technical Route**

Marcus Johnson took a completely different path to professional success. At 18, he decided university wasn't for him. "I was never great at academic studies," Marcus says. "But I was always good with my hands and loved figuring out how things work."

Instead of pursuing traditional higher education, Marcus applied for an apprenticeship as a trainee electrician with a local electrical contracting company. "My friends thought I was crazy," he remembers. "They were all going to university while I was learning to work with wires and circuits."

The apprenticeship lasted four years. Marcus worked during the day as a trainee under experienced electricians and attended technical school in the evenings. "The work was physically demanding," he says, "especially when we had to do overtime on big commercial projects. But I was learning valuable, practical skills every single day."

At 22 – the same age Ana was graduating from business school – Marcus became a fully qualified, licensed electrician. He took a job with a larger company, earning a competitive salary of $50,000 per year, significantly more than Ana was earning as a marketing trainee at the time.`
      },
      {
        type: "mc",
        q: "What profession did Marcus train for?",
        options: [
          "Electrician",
          "Plumber",
          "Surgeon"
        ],
        a: "Electrician",
        successtip: "✓ TECHNICAL PROFESSION!\nMarcus = trainee electrician → qualified electrician ⚡\nTechnical career path!"
      },
      {
        type: "mc",
        q: "At age 22, Marcus was earning ___ Ana at the same age.",
        options: [
          "more than",
          "less than",
          "the same as"
        ],
        a: "more than",
        successtip: "✓ SALARY COMPARISON!\nMarcus: $50K vs Ana: $35K 💰\nTechnical skills = competitive salary early!"
      },

      // ======================================================================
      // BLOQUE 3: Entrepreneurship & Growth (PREGUNTAS 5-6)
      // ======================================================================
      {
        type: "passage",
        passage: `But Marcus had bigger ambitions. At 26, he started his own electrical contracting business, specializing in renewable energy installations. "The field of solar energy was just taking off," he explains. "I saw an opportunity to combine my electrical skills with something that would help the environment."

The business grew rapidly. Marcus now employs 15 electricians and 3 trainees, working on projects ranging from residential solar installations to large commercial renewable energy systems. At 35 – the same age as Ana – Marcus's business generates over $2 million in annual revenue.

"I work long hours," Marcus says, "probably more overtime than when I was a trainee. But it's my business, my vision. I love my work, and I'm proud that we're training the next generation of electricians."`
      },
      {
        type: "mc",
        q: "Marcus's business now employs how many trainees?",
        options: ["3 trainees", "15 trainees", "No trainees"],
        a: "3 trainees",
        successtip: "✓ DETAIL COMPREHENSION!\n15 electricians + 3 trainees 📊\nMarcus trains the next generation!"
      },
      {
        type: "mc",
        q: "When Marcus says 'I love my work,' he uses 'work' as:",
        options: [
          "An uncountable noun referring to his activities",
          "A verb meaning he is working",
          "Incorrect grammar"
        ],
        a: "An uncountable noun referring to his activities",
        successtip: "✓ GRAMMAR SPOTTED!\n'Love my work' = work as noun (uncountable) 📋\nYou recognize the usage!"
      },

      // ======================================================================
      // BLOQUE 4: Comparing Paths (PREGUNTAS 7-8)
      // ======================================================================
      {
        type: "passage",
        passage: `**Different Paths, Equal Success**

Ana and Marcus represent two very different career trajectories – one corporate and international, the other technical and entrepreneurial. Ana's path involved formal education, internships, and climbing the corporate ladder. Marcus's involved apprenticeship, hands-on learning, and building his own business.

"I don't think one path is better than the other," Ana reflects. "We both found work that we're passionate about. We both built successful careers. We just took different routes to get there."

Marcus agrees. "Some people think that because I didn't go to university, my career is somehow less valuable. But I'm a highly skilled professional. My work requires extensive training, licensing, and constant learning about new technologies. Different doesn't mean inferior."

Both professionals credit their early experiences – Ana's internship and Marcus's apprenticeship – as foundational to their success. "Those early years teach you so much more than just job skills," Ana says. "They teach you work ethic, professional behavior, and how to handle challenges."`
      },
      {
        type: "mc",
        q: "What do Ana and Marcus both agree about career paths?",
        options: [
          "Different paths can lead to equal success",
          "University is always better than apprenticeship",
          "Corporate careers are more valuable"
        ],
        a: "Different paths can lead to equal success",
        successtip: "✓ MAIN THEME!\nDifferent ≠ inferior 🌟\nMultiple paths to success!"
      },
      {
        type: "input",
        q: "Both Ana and Marcus started with entry-level positions: Ana as an ___ and Marcus as a trainee.",
        a: "intern",
        accept: { ignoreCase: true, alts: ["internship"] },
        successtip: "✓ EMPLOYMENT TERMS!\nIntern & trainee = both entry-level 📚\nDifferent types, same starting point!"
      },

      // ======================================================================
      // BLOQUE 5: Final Message & Integration (PREGUNTAS 9-10)
      // ======================================================================
      {
        type: "passage",
        passage: `Today, both Ana and Marcus occasionally speak at career fairs, encouraging young people to consider all options. "Don't let society tell you there's only one path to success," Marcus tells students. "Find what you're passionate about, whether it requires a business degree or an electrical license. Both are valuable. Both can lead to amazing careers."

Ana adds, "Focus on finding work you love, not just a job that pays well. When you're passionate about your work, career success follows naturally. That's been true for both Marcus and me, despite our very different paths."

**Key Facts:**
- Ana: Started unpaid → $35K trainee salary → VP earning six figures
- Marcus: Trainee electrician → $50K qualified → $2M business owner
- Both age 35, both successful, both love their work
- Both emphasize: early experience (intern/trainee) was crucial
- Both worked overtime to develop their careers
- Different industries, different trajectories, equal fulfillment`
      },
      {
        type: "mc",
        q: "In the final paragraph, Ana says 'Focus on finding work you love, not just a job that pays well.' Why does she use 'work' instead of 'a job'?",
        options: [
          "Because 'work you love' refers to the type of activity/field (uncountable)",
          "It's a grammar mistake",
          "They mean exactly the same thing"
        ],
        a: "Because 'work you love' refers to the type of activity/field (uncountable)",
        tip: "🎯 ADVANCED GRAMMAR!\n\n'Work you love' = emphasizes the ACTIVITY/FIELD\n(uncountable, broader concept)\n\n'A job you love' = emphasizes the POSITION\n(countable, specific role)\n\nBoth are grammatically correct, but native speakers choose based on subtle meaning!\n\nAna's message is about finding the right FIELD, not just the right POSITION. That's why 'work' is perfect here! 🌟",
        successtip: "✓ SUBTLE MASTERY! 🌟\nYou understand the NUANCE!\n'Work' = activity/field (broader)\n'Job' = position (specific)\nBoth correct, different emphasis!"
      },
      {
        type: "mc",
        q: "This reading passage demonstrates all the concepts from Unit 1. How many can you identify?",
        options: [
          "All major concepts: job/work, intern, trainee, electrician, surgeon mentioned, salary, overtime, career trajectory",
          "Only 2-3 concepts",
          "None of the unit vocabulary"
        ],
        a: "All major concepts: job/work, intern, trainee, electrician, surgeon mentioned, salary, overtime, career trajectory",
        successtip: "✓ READING COMPLETE! 🎉\n\n📚 CONCEPTS INTEGRATED:\n\n✅ Job vs Work (multiple natural uses)\n✅ Intern (Ana's start)\n✅ Trainee (both paths)\n✅ Electrician (Marcus's profession)\n✅ Technical professions vocabulary\n✅ Salary ($35K, $50K, six figures)\n✅ Overtime (both worked extra hours)\n✅ Career trajectory (different paths)\n✅ Professional development\n✅ Work experience & career progression\n\nYou can now:\n• Understand professional discussions ✅\n• Recognize job/work usage in context ✅\n• Read about career paths confidently ✅\n\nAuthentic English reading mastered! 🌍💼"
      }
    ]
  },   // ==========================================================================
  // REVIEW SET - UNIT 1 INTEGRATION
  // ==========================================================================
  reviewSet: {
    id: "m7-u1-review",
    title: "⭐ Unit 1 Review: Talking about Jobs",
    category: "review",
    isReview: true,
    
    questions: [
      {
        type: "mc",
        q: "Which sentence is grammatically CORRECT?",
        options: [
          "I'm looking for work in the medical field",
          "I'm looking for a work in the medical field",
          "I'm looking for works in the medical field"
        ],
        a: "I'm looking for work in the medical field",
        tip: "🎯 REVIEW: JOB VS WORK!\n\nRemember:\n❌ 'A work' = incorrect (uncountable noun)\n❌ 'Works' = incorrect as plural of 'work' (noun)\n✅ 'Work' = uncountable (no article needed)\n✅ 'A job' = countable (article needed)\n\nBoth 'looking for work' and 'looking for a job' are correct, but never 'a work'!",
        successtip: "✓ GRAMMAR MASTERED!\nWork = uncountable noun 📚\nNo article needed!"
      },
      
      {
        type: "input",
        q: "After her ___ at the law firm, they offered her a full-time position as a trainee lawyer.",
        a: "internship",
        accept: { ignoreCase: true, alts: ["intern"] },
        successtip: "✓ EMPLOYMENT TERMS!\nInternship → trainee → full position 📈\nCareer progression!"
      },
      
      {
        type: "mc",
        q: "Complete professionally: 'I have extensive experience ___ as a surgical assistant in major hospitals.'",
        options: ["working", "jobbing", "works"],
        a: "working",
        successtip: "✓ PROFESSIONAL LANGUAGE!\n'Experience working as' = standard phrase 💼\nWork as verb in gerund form!"
      },
      
      {
        type: "order",
        q: "Traduce: El electricista calificado trabaja horas extras en proyectos de energía renovable",
        a: "The qualified electrician works overtime on renewable energy projects",
        successtip: "✓ INTEGRATION!\nProfession + work (verb) + overtime + field ⚡\nAll concepts together!"
      },
      
      {
        type: "mc",
        q: "Which profession typically requires an apprenticeship and licensing?",
        options: [
          "Electrician and plumber",
          "Only office workers",
          "Interns"
        ],
        a: "Electrician and plumber",
        successtip: "✓ TECHNICAL PROFESSIONS!\nElectrician & plumber = skilled trades 🔧⚡\nApprenticeships + certifications required!"
      },
      
      {
        type: "input",
        q: "She negotiated a competitive annual ___ of $85,000 plus benefits before accepting the offer.",
        a: "salary",
        accept: { ignoreCase: true },
        successtip: "✓ COMPENSATION!\nAnnual salary + benefits = complete package 💰\nProfessional negotiation!"
      },
      
      {
        type: "mc",
        q: "In a professional context, 'climbing the corporate ladder' means:",
        options: [
          "Getting promoted through increasingly senior positions",
          "Working in tall buildings",
          "Taking courses"
        ],
        a: "Getting promoted through increasingly senior positions",
        successtip: "✓ CAREER IDIOM!\nCorporate ladder = career progression metaphor 🪜\nMoving up through ranks!"
      },
      
      {
        type: "mc",
        q: "Which uses ALL terms correctly? 'After completing his apprenticeship, the trainee ___ became a qualified professional and now ___ for a major construction company.'",
        options: [
          "plumber / works",
          "plumber / jobs",
          "work / works"
        ],
        a: "plumber / works",
        successtip: "✓ COMPLETE INTEGRATION!\nTrainee plumber + works for = perfect! 🚰\nProfession (noun) + work (verb)!"
      },
      
      {
        type: "order",
        q: "Traduce: Mi trayectoria profesional me llevó de pasante a director en diez años",
        a: "My career trajectory took me from intern to director in ten years",
        successtip: "✓ CAREER PROGRESSION!\nIntern → Director = impressive trajectory 🚀\nProfessional growth story!"
      },
      
      {
        type: "mc",
        q: "FINAL MASTERY QUESTION: Read this professional bio: 'Dr. Sarah Martinez started her career as a medical intern, working long hours including significant overtime while learning from experienced surgeons. After completing her training, she became a qualified surgeon with a competitive salary. Today, she loves her work and trains new interns at the teaching hospital, helping shape their career trajectories.' How many Unit 1 concepts are correctly integrated?",
        options: [
          "Almost all: intern, overtime, surgeon, qualified, salary, work (noun), trains, career trajectory",
          "Only 2-3 concepts",
          "Many concepts but used incorrectly"
        ],
        a: "Almost all: intern, overtime, surgeon, qualified, salary, work (noun), trains, career trajectory",
        tip: "🌟 UNIT 1 COMPLETE MASTERY!\n\nThis bio naturally includes:\n\n✅ GRAMMAR:\n• 'Work' as noun ('her work')\n• Career terminology\n\n✅ EMPLOYMENT TERMS:\n• Intern → qualified professional\n• Overtime (extra hours)\n• Salary (compensation)\n• Trains (develops others)\n\n✅ PROFESSIONS:\n• Surgeon (medical specialist)\n• Teaching hospital context\n\n✅ CAREER CONCEPTS:\n• Career trajectory (path)\n• Professional progression\n• Mentorship\n\nAll used naturally and correctly - this is authentic professional English! 🎓",
        successtip: "🎉 UNIT 1 MASTERED! 🎉\n\n✅ JOB VS WORK\nYou understand when to use each!\n\n✅ TECHNICAL PROFESSIONS\nElectrician, plumber, surgeon - you know them!\n\n✅ EMPLOYMENT TERMS\nIntern, trainee, salary, overtime - mastered!\n\n✅ CAREER DISCUSSIONS\nTrajectory, progression, development - fluent!\n\n✅ PROFESSIONAL LANGUAGE\nYou can discuss careers confidently in English! 💼\n\n🌍 REAL-WORLD READY!\nYou can now:\n• Talk about your career professionally ✅\n• Understand job descriptions ✅\n• Discuss work experience naturally ✅\n• Network in professional settings ✅\n\nThis is B1-B2 level English! You're ready for international professional environments! 🌟\n\n¡Excelente trabajo! 💪"
      }
    ]
  }
};
